import { copy } from '@ohear/shared'
import { Badge, Button, Card, EmptyState, Spinner, useToast } from '@ohear/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import { api } from '../../lib/api'
import { useT } from '../../lib/i18n/use-t'

export const Route = createFileRoute('/admin/archival')({
  component: ArchivalAdminPage,
})

interface EligibleProject {
  id: string
  organization_id: string
  code: string | null
  name: string
  status: string
  delivered_days_ago: number
  created_at: string
}

interface ArchivedProject {
  id: string
  organization_id: string
  code: string | null
  name: string
  status: string
  archived_at: string
  cold_tier_completed_at: string | null
}

interface ListResponse {
  eligible: EligibleProject[]
  archived: ArchivedProject[]
  cold_tier_age_days: number
}

interface MoveResult {
  copied: number
  deleted: number
  bytes: number
  errors: Array<{ key: string; error: string }>
}

interface ArchiveResult {
  project_id: string
  archived_at: string | null
  cold_tier_completed_at: string | null
  media: MoveResult
  stems: MoveResult
  dry_run: boolean
}

const listKey = ['admin', 'archival', 'projects'] as const

function ArchivalAdminPage() {
  const qc = useQueryClient()
  const toast = useToast()

  const list = useQuery({
    queryKey: listKey,
    queryFn: () => api<ListResponse>('/api/v1/admin/archival/projects'),
  })

  const archive = useMutation({
    mutationFn: ({ id, dryRun }: { id: string; dryRun: boolean }) =>
      api<ArchiveResult>(`/api/v1/admin/archival/projects/${id}/archive`, {
        method: 'POST',
        body: JSON.stringify({ dry_run: dryRun }),
      }),
    onSuccess: (res) => {
      const total = res.media.copied + res.stems.copied
      const errCount = res.media.errors.length + res.stems.errors.length
      toast.show({
        severity: errCount === 0 ? 'success' : 'warn',
        title: res.dry_run
          ? `Dry run: ${total} objects (${formatBytes(res.media.bytes + res.stems.bytes)})`
          : `Archived ${total} objects (${formatBytes(res.media.bytes + res.stems.bytes)})`,
        description: errCount > 0 ? `${errCount} key(s) failed — check audit log.` : undefined,
      })
      void qc.invalidateQueries({ queryKey: listKey })
    },
    onError: (err) =>
      toast.show({
        severity: 'error',
        title: 'Archive failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
  })

  const restore = useMutation({
    mutationFn: ({ id, dryRun }: { id: string; dryRun: boolean }) =>
      api<ArchiveResult>(`/api/v1/admin/archival/projects/${id}/restore`, {
        method: 'POST',
        body: JSON.stringify({ dry_run: dryRun }),
      }),
    onSuccess: (res) => {
      toast.show({
        severity: 'success',
        title: res.dry_run
          ? `Dry run: ${res.media.copied} objects would be restored`
          : `Restored ${res.media.copied} objects`,
      })
      void qc.invalidateQueries({ queryKey: listKey })
    },
    onError: (err) =>
      toast.show({
        severity: 'error',
        title: 'Restore failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
  })

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-archival-title">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Platform admin
          </div>
          <h1 id="admin-archival-title" className="text-[22px] font-semibold tracking-tight m-0">
            Archival management
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Projects in <code>delivered</code> for &ge; {list.data?.cold_tier_age_days ?? 90} days
            are eligible for cold-tier archival. The daily worker handles this automatically; you
            can also fast-track or restore from here.
          </p>
        </header>

        <AdminNav />

        {list.isLoading ? (
          <div className="flex items-center gap-2 text-[var(--text-2)]">
            <Spinner /> Loading projects...
          </div>
        ) : null}

        {list.data ? (
          <>
            <EligibleTable
              rows={list.data.eligible}
              busyId={archive.isPending ? (archive.variables?.id ?? null) : null}
              onArchive={(id, dryRun) => archive.mutate({ id, dryRun })}
            />
            <ArchivedTable
              rows={list.data.archived}
              busyId={restore.isPending ? (restore.variables?.id ?? null) : null}
              onRestore={(id, dryRun) => restore.mutate({ id, dryRun })}
            />
          </>
        ) : null}
      </section>
    </AdminGuard>
  )
}

function EligibleTable({
  rows,
  busyId,
  onArchive,
}: {
  rows: EligibleProject[]
  busyId: string | null
  onArchive: (id: string, dryRun: boolean) => void
}) {
  return (
    <Card title={`Eligible for archival (${rows.length})`}>
      {rows.length === 0 ? (
        <EmptyState title="Nothing to archive">
          No delivered projects are old enough yet.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
            <thead
              className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
              style={{ background: 'var(--bg-3)' }}
            >
              <tr>
                <th className="px-4 py-2 font-medium">Code</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Delivered</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const busy = busyId === p.id
                return (
                  <tr key={p.id} className="transition-colors hover:bg-[var(--bg-3)]">
                    <td
                      className="px-4 py-2 font-mono text-[12px] border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {p.code ?? '—'}
                    </td>
                    <td
                      className="px-4 py-2 font-medium border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {p.name}
                    </td>
                    <td className="px-4 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                      <Badge variant="amber">{p.status}</Badge>
                    </td>
                    <td
                      className="px-4 py-2 text-[var(--text-2)] tabular-nums border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {p.delivered_days_ago}d ago
                    </td>
                    <td
                      className="px-4 py-2 text-right whitespace-nowrap border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={busy}
                        onClick={() => onArchive(p.id, true)}
                      >
                        Dry run
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        loading={busy}
                        onClick={() => onArchive(p.id, false)}
                        className="ml-2"
                      >
                        Archive now
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function ArchivedTable({
  rows,
  busyId,
  onRestore,
}: {
  rows: ArchivedProject[]
  busyId: string | null
  onRestore: (id: string, dryRun: boolean) => void
}) {
  const t = useT()
  return (
    <Card title={`Archived projects (${rows.length})`}>
      {rows.length === 0 ? (
        <EmptyState title={t(copy.admin.EMPTY.archival_nothing.title)}>
          {t(copy.admin.EMPTY.archival_nothing.body)}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
            <thead
              className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
              style={{ background: 'var(--bg-3)' }}
            >
              <tr>
                <th className="px-4 py-2 font-medium">Code</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Archived</th>
                <th className="px-4 py-2 font-medium">Cold tier</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const busy = busyId === p.id
                return (
                  <tr key={p.id} className="transition-colors hover:bg-[var(--bg-3)]">
                    <td
                      className="px-4 py-2 font-mono text-[12px] border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {p.code ?? '—'}
                    </td>
                    <td
                      className="px-4 py-2 font-medium border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {p.name}
                    </td>
                    <td
                      className="px-4 py-2 text-[var(--text-2)] tabular-nums border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {formatRelative(p.archived_at)}
                    </td>
                    <td className="px-4 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                      {p.cold_tier_completed_at ? (
                        <Badge variant="green">complete</Badge>
                      ) : (
                        <Badge variant="amber">in progress</Badge>
                      )}
                    </td>
                    <td
                      className="px-4 py-2 text-right whitespace-nowrap border-t"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={busy}
                        onClick={() => onRestore(p.id, true)}
                      >
                        Dry run
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        loading={busy}
                        onClick={() => onRestore(p.id, false)}
                        className="ml-2"
                      >
                        Restore
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}
